import { isTen, type CardId } from './cards';
import type { RulesConfig } from './rules';
import { rulesStats } from './rules';
import type {
  GameEvent,
  HandPhase,
  HandResult,
  MatchPhase,
  MatchState,
  Play,
  Seat,
  TaggedEvent,
  Team,
  TrickRecord,
  TrumpState,
} from './types';

export interface PublicHandView {
  handNumber: number;
  handId: string;
  dealer: Seat;
  phase: HandPhase;
  leader: Seat;
  active: Seat;
  trickIndex: number;
  trick: Play[];
  lastTrick: TrickRecord | null;
  trump: TrumpState;
  /** Cards held per seat, including a face-down Band reserve. */
  handCounts: number[];
  tricksWon: [number, number];
  tens: [number, number];
  capturedTens: [CardId[], CardId[]];
  handSize: number;
  totalTens: number;
}

export interface PublicMatchView {
  rules: RulesConfig;
  matchId: string;
  phase: MatchPhase;
  dealer: Seat;
  score: [number, number];
  handsPlayed: number;
  hand: PublicHandView;
  lastResult: HandResult | null;
  winner: Team | null;
}

export interface PrivateSeatState {
  seat: Seat;
  hand: CardId[];
  reserved: CardId | null;
}

/** Everything one seat is allowed to know. Bots and legality checks only ever receive this. */
export interface SeatView extends PublicMatchView {
  seat: Seat;
  myHand: CardId[];
  myReserved: CardId | null;
}

const copyPlay = (p: Play): Play => ({ seat: p.seat, cardId: p.cardId, order: p.order });

/** Public projection, built by whitelisting — deck order, hands and the hidden reserve never leave. */
export function toPublic(state: MatchState): PublicMatchView {
  const { hand, rules } = state;
  const stats = rulesStats(rules);
  const reserverSeat = hand.trump.reserverSeat;
  const last = hand.tricks.length > 0 ? hand.tricks[hand.tricks.length - 1]! : null;
  const trump = hand.trump;
  return {
    rules: { ...rules },
    matchId: state.matchId,
    phase: state.phase,
    dealer: state.dealer,
    score: [state.score[0], state.score[1]],
    handsPlayed: state.handsPlayed,
    lastResult: state.results.length > 0 ? { ...state.results[state.results.length - 1]! } : null,
    winner: state.winner,
    hand: {
      handNumber: hand.handNumber,
      handId: hand.handId,
      dealer: hand.dealer,
      phase: hand.phase,
      leader: hand.leader,
      active: hand.active,
      trickIndex: hand.trickIndex,
      trick: hand.trick.map(copyPlay),
      lastTrick: last
        ? {
            trickIndex: last.trickIndex,
            plays: last.plays.map(copyPlay),
            winner: last.winner,
            tens: last.tens,
          }
        : null,
      trump: {
        mode: trump.mode,
        status: trump.status,
        suit: trump.status === 'HIDDEN' ? null : trump.suit,
        reserverSeat: trump.reserverSeat,
        cardId: trump.status === 'HIDDEN' ? null : trump.cardId,
        activatedAt: trump.activatedAt ? { ...trump.activatedAt } : null,
        requestedBy: trump.requestedBy,
      },
      handCounts: hand.hands.map(
        (cards, seat) => cards.length + (hand.reserved !== null && seat === reserverSeat ? 1 : 0),
      ),
      tricksWon: [hand.tricksWon[0], hand.tricksWon[1]],
      tens: [hand.captured[0].filter(isTen).length, hand.captured[1].filter(isTen).length],
      capturedTens: [hand.captured[0].filter(isTen), hand.captured[1].filter(isTen)],
      handSize: stats.handSize,
      totalTens: stats.tens,
    },
  };
}

export function toPrivate(state: MatchState, seat: Seat): PrivateSeatState {
  const { hand } = state;
  return {
    seat,
    hand: [...(hand.hands[seat] ?? [])],
    reserved: hand.trump.reserverSeat === seat ? hand.reserved : null,
  };
}

export function toSeatView(state: MatchState, seat: Seat): SeatView {
  return seatViewFrom(toPublic(state), toPrivate(state, seat));
}

/** Client-side reconstruction from the two documents a seat can read. Must equal toSeatView(). */
export function seatViewFrom(pub: PublicMatchView, priv: PrivateSeatState): SeatView {
  return { ...pub, seat: priv.seat, myHand: [...priv.hand], myReserved: priv.reserved };
}

export function eventsFor(events: readonly TaggedEvent[], audience: 'public' | Seat): GameEvent[] {
  return events.filter((ev) => ev.to === audience).map((ev) => ev.e);
}
